import { Dialog, Row } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { DetailsItem, HeaderDialog } from 'components';
import DetailsCard from 'components/foundation/DetailsCard/DetailsCard';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import { useOpenable } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import useEmployeeNameFormat, {
  employeeNameFormatLabelLookup,
} from 'hooks/settings/local-settings/useEmployeeNameFormat';
import useKonamiCode from 'hooks/utils/useKonamiCode/useKonamiCode';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import { t } from 'utils/localize';
import DashboardSettingsDetails from './DashboardSettingsDetails/DashboardSettingsDetails';
import EmployeeNameFormatForm from './EmployeeNameFormatForm/EmployeeNameFormatForm';
import GustoSettingsDetail from './GustoSettingsDetails/GustoSettingsDetail';
import MemberSettingsBlobDetails from './MemberSettingsBlobDetails/MemberSettingsBlobDetails';
import MenuCustomizationsForm from './MenuCustomizationsForm';
import OrganizationSettingsDetails from './OrganizationSettingsDetails/OrganizationSettingsDetails';
import './Preferences.scss';

export interface IPreferencesProps {
  className?: ClassName;
}

interface ISecretMenuItem {
  key: string;
  title: string;
  description: string;
  dialogTitle: string;
  Component: ComponentType<{ onClose: () => void }>;
}

const SECRET_MENU_ITEMS: ISecretMenuItem[] = [
  {
    key: 'member',
    title: 'Member Settings',
    description: 'View and adjust member settings.',
    dialogTitle: 'Member Settings',
    Component: MemberSettingsBlobDetails,
  },
  {
    key: 'organization',
    title: 'Organization Settings',
    description: 'View current organization settings.',
    dialogTitle: 'Organization Settings',
    Component: OrganizationSettingsDetails,
  },
  {
    key: 'dashboard',
    title: 'Dashboard Settings',
    description: 'View and adjust dashboard settings.',
    dialogTitle: 'Dashboard Settings',
    Component: DashboardSettingsDetails,
  },
  {
    key: 'gusto',
    title: 'Gusto Settings',
    description: 'View and adjust gusto settings.',
    dialogTitle: 'Gusto Disconnect',
    Component: GustoSettingsDetail,
  },
];

function Preferences({ className }: IPreferencesProps) {
  const [employeeNameFormat] = useEmployeeNameFormat();
  const employeeNameOpenable = useOpenable();
  const customizeMenuOpenable = useOpenable();
  const [showSecretMenu, setShowSecretMenu] = useState(false);
  const [activeSecretDialog, setActiveSecretDialog] = useState<string | null>(null);
  const brand = useBrandTitle();

  useKonamiCode(() => setShowSecretMenu(true));

  const closeSecretDialog = () => setActiveSecretDialog(null);

  return (
    <div className={classNames('preferences', className)}>
      <SectionHeader className={'pb-2'}>{t('Formatting')}</SectionHeader>
      <Row>
        <DetailsCard>
          <DetailsItem
            onClick={employeeNameOpenable.open}
            hint={t(
              'This determines how employee names should appear in {{brand}} reports. Since many reports are sorted alphabetically by name, the order in which employees appear in a report may change depending on the format.',
              { brand }
            )}
          >
            <HeaderText title={t('Employee Name Format')}>
              {employeeNameFormatLabelLookup[employeeNameFormat]}
            </HeaderText>
          </DetailsItem>
        </DetailsCard>
      </Row>
      <Dialog isOpen={employeeNameOpenable.isOpen} onClose={employeeNameOpenable.close}>
        <EmployeeNameFormatForm className="preferences-name-format-dialog-form" onSubmit={employeeNameOpenable.close} />
      </Dialog>
      <SectionHeader className={'pt-8 pb-2'}>{t('Navigation')}</SectionHeader>
      <Row>
        <DetailsCard>
          <DetailsItem onClick={customizeMenuOpenable.open}>
            <HeaderText title={t('Customize Menu')}>{t('Hide and reorder main menu items.')}</HeaderText>
          </DetailsItem>
        </DetailsCard>
      </Row>

      {showSecretMenu && (
        <>
          <SectionHeader className={'pt-8 pb-2'}>{t('Secret Menu')}</SectionHeader>
          <Row>
            <DetailsCard>
              {SECRET_MENU_ITEMS.map((item) => (
                <DetailsItem key={item.key} onClick={() => setActiveSecretDialog(item.key)}>
                  <HeaderText title={t(item.title)}>{t(item.description)}</HeaderText>
                </DetailsItem>
              ))}
            </DetailsCard>
          </Row>
        </>
      )}
      <HeaderDialog
        title={t('Main Menu')}
        divider={false}
        isOpen={customizeMenuOpenable.isOpen}
        onClose={customizeMenuOpenable.close}
      >
        <MenuCustomizationsForm onSubmit={customizeMenuOpenable.close} />
      </HeaderDialog>
      {SECRET_MENU_ITEMS.map(({ key, dialogTitle, Component }) => (
        <HeaderDialog
          key={key}
          title={t(dialogTitle)}
          divider={false}
          isOpen={activeSecretDialog === key}
          onClose={closeSecretDialog}
        >
          <Component onClose={closeSecretDialog} />
        </HeaderDialog>
      ))}
    </div>
  );
}

export default Preferences;
